import { AfterContentInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatListModule } from '@angular/material/list';
import { ActivityInterface } from 'src/app/models/activity.interface';
import { getUrl } from 'aws-amplify/storage';

@Component({
    selector: 'app-activity',
    templateUrl: './activity.component.html',
    styleUrls: ['./activity.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [CommonModule, RouterModule, MatCardModule, MatListModule]
})
export class ActivityComponent implements OnInit, AfterContentInit, OnChanges {
  @Input() activity!: ActivityInterface;
  @Input() pictureUrl!: string;

  // Cached unique pictures to avoid recalculating on every change detection
  uniquePictures: any[] = [];

  // id!: string;
  // name!: string;
  // topPick: boolean = false;
  // favorite: boolean = false;
  // picUrl!: string;
  // mainActivityType!: string;
  // description!: string;
  // duration!: string;
  // amenityTags: string[] = [];
  // bookingTag!: number;
  // bookingTagActive: boolean = false;
  // sellOut: boolean = false;
  // searchTags: string[] = [];
  // rating!: number;
  // pricePerPerson!: number;
  // discountedPrice!: number;
  // peoplePerPrice!: number;
  // currencyType!: string;
  pictureDisplay!: string;

  reviews!: string;
  // bookingTagText!: string;
  // groupWording!: string;

  // retailPricePhrase!: string;
  // salePricePhrase!: string;

  // amenitiesBar: string[] = [];

  constructor(public router: Router, private cdr: ChangeDetectorRef) {}

  ngOnChanges(changes: SimpleChanges): void {
    // Update unique pictures when activity changes
    if (changes['activity']) {
      if (!changes['activity'].firstChange) {
        this.updateUniquePictures();
      } else {
        // For first change, update after ngOnInit
        setTimeout(() => this.updateUniquePictures(), 0);
      }
    }
  }

  ngOnInit(): void {
    // Initialize unique pictures
    this.updateUniquePictures();

    this.activity.reviews = 12;
    if (this.activity.reviews != 1) {
      this.reviews = " (" + this.activity.reviews + " reviews)";
    } else {
      this.reviews = " (" + this.activity.reviews + " review)";
    }
  }

  ngAfterContentInit() {
    // Pictures are already processed in ngOnInit and ngOnChanges
  }

  async updateUniquePictures(): Promise<void> {
    // Filter out duplicates and invalid pictures
    const seenUrls = new Set<string>();
    const validPictures: any[] = [];
    
    if (this.activity?.picUrl && Array.isArray(this.activity.picUrl)) {
      this.activity.picUrl.forEach((pic: any, index: number) => {
        if (pic && pic.url) {
          const normalizedUrl = pic.url.trim().toLowerCase();
          if (normalizedUrl && !seenUrls.has(normalizedUrl)) {
            seenUrls.add(normalizedUrl);
            validPictures.push({
              ...pic,
              _trackId: pic.id || `${this.activity.id}-${index}-${normalizedUrl.substring(0, 10)}`,
              _originalUrl: pic.url // Keep original URL for signing
            });
          }
        }
      });
    }
    
    // Only process first picture for the card
    const pictureToProcess = validPictures.slice(0, 1);
    
    if (pictureToProcess.length === 0) {
      this.uniquePictures = [];
      this.cdr.markForCheck();
      return;
    }

    // Convert URLs to signed URLs
    const processedPictures = await Promise.all(
      pictureToProcess.map(async (pic) => {
        const originalUrl = pic._originalUrl || pic.url;
        let signedUrl = originalUrl;
        
        // If URL doesn't start with http/https, it's likely a storage key
        if (!originalUrl.startsWith('http://') && !originalUrl.startsWith('https://')) {
          try {
            const urlResult = await getUrl({
              key: originalUrl,
              options: {
                accessLevel: 'guest'
              }
            });
            signedUrl = urlResult.url.toString();
            console.log(`[ACTIVITY CARD] Got signed URL for activity "${this.activity.name}": ${signedUrl}`);
          } catch (urlError) {
            console.warn(`[ACTIVITY CARD] Failed to get signed URL for key "${originalUrl}":`, urlError);
            signedUrl = originalUrl; // Fallback to original URL
          }
        } else {
          // Already a full URL, but check if it's an S3 URL that might need signing
          const urlStr = originalUrl;
          if (urlStr.includes('amazonaws.com') || urlStr.includes('s3')) {
            try {
              // Try to extract key from URL and get a fresh signed URL
              const keyMatch = urlStr.match(/activity-pictures\/[^?]+|activity-images\/[^?]+/);
              if (keyMatch) {
                const key = keyMatch[0];
                const urlResult = await getUrl({
                  key: key,
                  options: {
                    accessLevel: 'guest'
                  }
                });
                signedUrl = urlResult.url.toString();
                console.log(`[ACTIVITY CARD] Got signed URL from S3 URL for activity "${this.activity.name}"`);
              }
            } catch (urlError) {
              console.warn(`[ACTIVITY CARD] Failed to get signed URL from S3 URL:`, urlError);
              signedUrl = originalUrl; // Fallback to original URL
            }
          }
        }
        
        return {
          ...pic,
          url: signedUrl
        };
      })
    );

    this.uniquePictures = processedPictures;
    this.cdr.markForCheck();
  }

  onClickGo() {
    // Navigate to booking page - route is 'booking/:id'
    this.router.navigate(['/booking', this.activity.id]);
  }

  handleImageError(event: any, activity: ActivityInterface) {
    const imgElement = event.target as HTMLImageElement;
    const imageUrl = activity.picUrl?.[0]?.url || imgElement.src;
    
    console.error(`[IMAGE ERROR] Failed to load image for activity "${activity.name}":`, {
      imageUrl: imageUrl,
      activityId: activity.id,
      error: event
    });
    
    // Replace image with placeholder message
    if (imgElement && imgElement.parentElement) {
      imgElement.style.display = 'none';
      const placeholder = document.createElement('div');
      placeholder.className = imgElement.className;
      placeholder.style.cssText = 'display: flex; align-items: center; justify-content: center; background-color: #f5f5f5; color: #666; font-size: 14px; min-height: 200px; width: 100%;';
      placeholder.textContent = 'Images coming soon';
      imgElement.parentElement.insertBefore(placeholder, imgElement);
    }
  }

}
